import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { MoreActions } from '../MoreActions/MoreActions';
import type { IMoreActionItem } from '../MoreActions/MoreActions.types';
import { formatKnowledgeDocTitle } from '../../utils/parseKnowledgeDocs';
import { saveKnowledgeDoc, getKnowledgeDoc } from '../../services/knowledgeDocsService';
import { IKnowledgeDocCardProps } from './KnowledgeDocCard.type';
import { Permissions } from "../../constants/permissions";

// Chat card for a generated knowledge document. Rendered in both Ask and Agent mode.
// Saving is explicit; open/share auto-persist first (idempotent on knowledgeDocId+version)
// because both need a real DB id, then act on the returned record.
export const KnowledgeDocCard: React.FC<IKnowledgeDocCardProps> = ({
  title,
  identifier,
  project,
  content,
  version,
  messageId,
  threadId,
  className = '',
  isStreaming = false,
  createdFrom = 'ask',
  savedDocId,
  onSaved,
  onShare,
  onOpen,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localDocId, setLocalDocId] = useState<string | undefined>(savedDocId);
  const menuRef = useRef<HTMLButtonElement>(null);
  const { projectId } = useParams();
  const hasUpdatePermission = project?.permissions?.includes(Permissions.UpdateProjectPermission);

  // The saved-doc lookup loads asynchronously after mount, so adopt savedDocId when it
  // resolves (restores the saved state on page reload).
  useEffect(() => {
    if (savedDocId) setLocalDocId(savedDocId);
  }, [savedDocId]);

  const isSaved = Boolean(localDocId);

  // The ONLY path that persists. Saving is always explicit — open/share never write.
  const handleSave = async () => {
    if (saving || !projectId || !identifier) return;
    setSaving(true);
    try {
      const record = await saveKnowledgeDoc({
        projectId,
        knowledgeDocId: identifier,
        title,
        content: content || '',
        version,
        messageId,
        threadId,
        createdFrom,
      });
      const dbId = record._id || record.id;
      if (dbId) {
        setLocalDocId(dbId);
        onSaved?.(dbId);
        window.dispatchEvent(new Event('knowledgeDocSaved'));
      }
    } catch (error) {
      console.error('Failed to save knowledge document:', error);
      alert('Failed to save knowledge document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Open in the inline side panel (preferred). Falls back to a new tab when the parent
  // doesn't provide an onOpen handler (e.g. contexts without the panel).
  const handleOpen = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (onOpen) {
      onOpen({ title, version, content, identifier, docId: localDocId, messageId });
      return;
    }
    handleOpenInNewTab();
  };

  // Open/download don't require a save: a saved doc resolves by Mongo _id, an unsaved one
  // by its source message (durable copy). Only Share needs a save (durable public link).
  const handleOpenInNewTab = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!projectId) return;
    const url = localDocId
      ? `/project/${projectId}/knowledge-doc/${localDocId}`
      : messageId
        ? `/project/${projectId}/message/${messageId}/knowledge-doc/${identifier}`
        : null;
    if (!url) return;
    window.open(url, '_blank');
  };

  const triggerDownload = (markdown: string) => {
    const blob = new Blob([markdown || ''], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeTitle = (title || 'knowledge-document').trim();
    link.download = safeTitle.toLowerCase().endsWith('.md') ? safeTitle : `${safeTitle}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // Saved: fetch through the GET endpoint so lastAccessedAt is stamped (and we get the
    // persisted content). Unsaved: no row to stamp — build from in-memory content.
    if (localDocId && projectId) {
      try {
        const record = await getKnowledgeDoc(projectId, localDocId);
        triggerDownload(record.content || content || '');
        return;
      } catch (error) {
        console.error('Failed to download knowledge document:', error);
        // Fall back to in-memory content so the download still succeeds.
      }
    }
    triggerDownload(content || '');
  };

  const handleShare = () => {
    if (!onShare || !localDocId) return;
    onShare({
      _id: localDocId,
      project_id: projectId || '',
      title,
      content: content || '',
      type: 'text/markdown',
      knowledge_doc_id: identifier,
      version,
    });
  };

  const menuItems: IMoreActionItem[] = [
    { id: 'open', label: 'Open in new tab', onClick: () => handleOpenInNewTab(undefined) },
    { id: 'download', label: 'Download', onClick: () => handleDownload(undefined) },
    { id: 'save', label: saving ? 'Saving...' : isSaved ? 'Saved' : 'Save', onClick: handleSave, disabled: saving || isSaved || !hasUpdatePermission },
    ...(onShare
      ? [{ id: 'share', label: isSaved ? 'Share' : 'Save to share', onClick: handleShare, disabled: !isSaved || !hasUpdatePermission } as IMoreActionItem]
      : []),
  ];

  return (
    <div
      className={`
        bg-bg-brand-primary-alt border border-utility-brand-100-alt rounded-bg-v2-md
        px-bg-v2-lg py-bg-v2-md flex items-center gap-bg-v2-lg
        ${isStreaming ? 'cursor-default relative overflow-hidden' : 'cursor-pointer hover:shadow-bg-v2-sm'}
        transition-shadow ${className}
      `}
      onClick={() => !isStreaming && handleOpen()}
    >
      <div className={`flex items-center gap-bg-v2-lg flex-1 min-w-0 ${isStreaming ? 'skeleton-pulse' : ''}`}>
        <div className="relative flex-shrink-0">
          <div className="w-[34px] h-[34px] rounded-full bg-bg-primary flex items-center justify-center shadow-bg-v2-xs">
            <Icon name={IconName.Document} size="sm" className="text-border-brand-icn" inheritColor />
          </div>
        </div>
        <div className="flex flex-col gap-bg-v2-xxs flex-1 min-w-0">
          <Text variant="text-sm" weight="medium" color="primary" className="truncate" as={'span'}>
            {formatKnowledgeDocTitle(title, version)}
          </Text>
          <div className="flex items-center gap-bg-v2-xs">
            <Text variant="text-xs" weight="medium" color="tertiary" as={'span'}>
              Document
            </Text>
          </div>
        </div>
      </div>

      {!isStreaming && (
        <div className="flex items-center gap-bg-v2-xs flex-shrink-0">
          <button
            onClick={handleOpen}
            title="Open"
            className="w-bg-v2-4xl h-bg-v2-4xl rounded-full flex items-center justify-center hover:bg-utility-brand-100-alt transition-colors"
            aria-label="Open"
          >
            <Icon name={IconName.ArrowNarrowUpRight} size="md" className="text-fg-quaternary" inheritColor />
          </button>
          <div className="relative">
            <button
              ref={menuRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="w-bg-v2-4xl h-bg-v2-4xl rounded-full flex items-center justify-center hover:bg-utility-brand-100-alt transition-colors"
              aria-label="More options"
            >
              <Icon name={IconName.More} size="sm" variant={'secondary'} />
            </button>
            <MoreActions
              items={menuItems}
              isOpen={menuOpen}
              onClose={() => setMenuOpen(false)}
              position="bottom-left"
              triggerRef={menuRef}
            />
          </div>
        </div>
      )}
    </div>
  );
};
